/**
 * Analytics event dispatching.
 *
 * Dispatches CustomEvents on the host page's window object AND pushes
 * to the GTM dataLayer. This lets course operators set up Google Analytics
 * conversions, Facebook Pixel events, or any other tracking without
 * modifying the widget code.
 */

/**
 * Dispatch a widget event on the host page window.
 * Events are namespaced as `currents:{name}` for CustomEvent
 * and `currents_{name}` for GTM dataLayer.
 */
export function dispatchWidgetEvent(
  name: string,
  detail?: Record<string, unknown>
): void {
  // CustomEvent on window — for any JS listener
  try {
    window.dispatchEvent(
      new CustomEvent(`currents:${name}`, {
        detail: detail || {},
        bubbles: false,
        cancelable: false,
      })
    )
  } catch {
    // CustomEvent may not be supported in very old browsers
  }

  // GTM dataLayer push
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: `currents_${name}`,
      ...detail,
    })
  } catch {
    // dataLayer push failed — non-critical
  }
}

// ─── Convenience helpers ──────────────────────────────────────────────────

export const analytics = {
  widgetOpened: () => dispatchWidgetEvent('widget_opened'),

  widgetClosed: (reason: string) =>
    dispatchWidgetEvent('widget_closed', { reason }),

  stepChanged: (step: string, stepIndex: number) =>
    dispatchWidgetEvent('step_changed', { step, stepIndex }),

  dateSelected: (date: string) =>
    dispatchWidgetEvent('date_selected', { date }),

  timeSelected: (time: string, price: number) =>
    dispatchWidgetEvent('time_selected', { time, price }),

  bookingStarted: () => dispatchWidgetEvent('booking_started'),

  paymentCompleted: (amount: number, currency: string) =>
    dispatchWidgetEvent('payment_completed', { amount, currency }),

  bookingConfirmed: (bookingId: string) =>
    dispatchWidgetEvent('booking_confirmed', { booking_id: bookingId }),
}
